import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '@/contexts/app-context';
import { sendAsGoal } from '@/hooks/use-ab-tests';
import { DynamicWidget } from '../../dynamic-widget/dynamic-widget';
import { removeTrailingSlash } from '@/utils/strings';
import './style.css';

export type ConsumerloanCalculatorAppProps = {
    labels: object;
    settings: Record<string, unknown> & {
        backgroundImage?: 'true' | 'false';
        anchorLink?: string;
        callbackUrl?: string;
        secciLink?: {
            url: string;
        };
        commonTermsLink?: {
            url: string;
        };
        legacyConsumerloanBaseUrl?:
            | string
            | {
                  url: string;
              };
    };
    abFirstGoal?: {
        useAsGoal: boolean;
        abTestUuid: string;
    };
};

const WIDGET = 'consumerloan-calculator-app';
const CONTAINER_ID = `${WIDGET}-container`;

// The external widget hardcodes its title as an <h1> (styled.h1, not
// configurable), which collides with the page's own <h1>. Re-tag it as an <h2>
// after the widget renders. A real tag swap rather than role/aria-level so that
// tooling reading tag names sees it too. See docs/handover/02-services.md.
const demoteWidgetHeadings = (containers: HTMLElement[]) => {
    const outsideWidget = (heading: HTMLElement) => !containers.some(container => container.contains(heading));

    // Never leave the page without an <h1>.
    if (![...document.querySelectorAll<HTMLElement>('h1')].some(outsideWidget)) {
        return;
    }

    containers.forEach(container =>
        container.querySelectorAll('h1').forEach(h1 => {
            const h2 = document.createElement('h2');
            [...h1.attributes].forEach(({ name, value }) => h2.setAttribute(name, value));
            h2.append(...h1.childNodes);
            h1.replaceWith(h2);
        })
    );
};

export const ConsumerloanCalculatorApp: React.FC<ConsumerloanCalculatorAppProps> = ({ labels, settings, abFirstGoal }) => {
    const { pathname } = useLocation();
    const context = useContext(AppContext);
    const protocol = context.isProd ? 'https' : 'http';
    const url = `${protocol}://${context.host}`;
    const [isClient, setIsClient] = useState(false);

    const handleCounterModuleSubmit = () => {
        if (abFirstGoal?.useAsGoal) {
            sendAsGoal(abFirstGoal.abTestUuid);
        }
    };

    const handleCounterModuleBeginApply = () => {
        if (abFirstGoal?.useAsGoal) {
            sendAsGoal(abFirstGoal?.abTestUuid, 'started');
        }
    };

    useEffect(() => {
        setIsClient(true);

        document.body.addEventListener('counter-module-submit', handleCounterModuleSubmit);
        document.body.addEventListener('counter-module-begin-apply', handleCounterModuleBeginApply);

        return () => {
            document.body.removeEventListener('counter-module-submit', handleCounterModuleSubmit);
            document.body.removeEventListener('counter-module-begin-apply', handleCounterModuleBeginApply);
        };
    });

    // As of writing, this entire effect is just for the purpose
    // of making this widget's <h1> an <h2> to avoid WCAG violations.
    useEffect(() => {
        let observers: MutationObserver[] = [];

        // The title appears only once the widget's API call resolves, and the
        // widget remounts on route changes — it announces every mount with
        // `counter-module-init` on document.body, dispatched from its own mount
        // effect (grep the bundles in src/public). querySelectorAll rather than
        // getElementById: the widget's own de-dupe races its async first render,
        // so two blocks on a page really do yield two containers sharing an id.
        const watch = () => {
            observers.forEach(observer => observer.disconnect());

            const containers = [...document.querySelectorAll<HTMLElement>(`#${CONTAINER_ID}`)];
            demoteWidgetHeadings(containers);

            observers = containers.map(container => {
                const observer = new MutationObserver(() => demoteWidgetHeadings(containers));
                observer.observe(container, { childList: true, subtree: true });
                return observer;
            });
        };

        watch();
        document.body.addEventListener('counter-module-init', watch);

        return () => {
            observers.forEach(observer => observer.disconnect());
            document.body.removeEventListener('counter-module-init', watch);
        };
    }, []);

    const getFormattedLocale = () => {
        const localeParts = context.locale.split('-');
        return `${localeParts[0]}_${localeParts[1]?.toUpperCase()}`;
    };

    const getCallbackUrl = () => {
        if (!settings.callbackUrl) {
            return `${url}${pathname}`;
        }

        const callbackUrl = settings.callbackUrl.replace(/http?s:\/\/[^/]+/, '');

        return removeTrailingSlash(`${url}${callbackUrl}`);
    };

    const getLegacyConsumerloanBaseUrl = () => {
        if (typeof settings.legacyConsumerloanBaseUrl === 'string' && settings.legacyConsumerloanBaseUrl !== '') {
            return settings.legacyConsumerloanBaseUrl.replaceAll('&amp;', '&');
        }

        if (typeof settings.legacyConsumerloanBaseUrl !== 'string' && settings.legacyConsumerloanBaseUrl?.url) {
            return settings.legacyConsumerloanBaseUrl.url.replaceAll('&amp;', '&');
        }

        return 'https://apply.resursbank.se/epl';
    };

    const getSettings = () => ({
        ...settings,
        callbackUrl: getCallbackUrl(),
        backgroundImage: settings?.backgroundImage || 'true',
        showConsolidation: settings.showConsolidation || 'false',
        useConsolidation: settings.useConsolidation || 'false',
        secciLink: settings?.secciLink?.url ? settings.secciLink.url?.replaceAll('&amp;', '&') : '',
        commonTermsLink: settings?.commonTermsLink?.url ? settings.commonTermsLink.url?.replaceAll('&amp;', '&') : '',
        legacyConsumerloanBaseUrl: getLegacyConsumerloanBaseUrl(),
        countryCode: context.country.toUpperCase(),
        locale: getFormattedLocale(),
        lang: context.language.toUpperCase(),
        currency: context.currency,
    });

    return isClient ? (
        <DynamicWidget
            next
            widget={WIDGET}
            id={settings.anchorLink}
            dataAttributes={{
                labels,
                settings: getSettings(),
            }}
        />
    ) : null;
};
